import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, FileText, TrendingUp, Umbrella, Heart, Gift, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { getCurrentCoordinates } from '../../utils/geolocation';
import { getCheckoutErrorMessage } from '../../utils/attendanceErrors';

const getAttendanceStatus = (todayAttendance) => {
  if (!todayAttendance || !todayAttendance.checkInTime) {
    return {
      color: 'bg-red-500',
      textColor: 'text-red-700',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    };
  }
  if (todayAttendance.checkInTime && !todayAttendance.checkOutTime) {
    return {
      color: 'bg-orange-500',
      textColor: 'text-orange-700',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    };
  }
  return {
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  };
};

const Dashboard = () => {
  useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchTodayAttendance();
    
    // Auto-refresh dashboard data every 60 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleAttendanceUpdated = () => {
      fetchDashboardData();
      fetchTodayAttendance();
    };

    window.addEventListener('attendance-updated', handleAttendanceUpdated);

    return () => {
      window.removeEventListener('attendance-updated', handleAttendanceUpdated);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Get current month attendance using IST timezone
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
      const istDate = new Date(now.getTime() + istOffset);
      const month = istDate.getUTCMonth() + 1;
      const year = istDate.getUTCFullYear();
      
      const [attendanceRes, leavesRes, tasksRes, leaveBalanceRes] = await Promise.all([
        api.get(`/employee/attendance/history?month=${month}&year=${year}`),
        api.get(`/employee/leave?month=${month}&year=${year}`),
        api.get('/employee/tasks'),
        api.get('/employee/leave-balance')
      ]);

      const attendances = attendanceRes.data.attendances || [];
      const leaves = leavesRes.data.leaves || [];
      const tasks = tasksRes.data.tasks || [];

      // Calculate statistics
      const fullDays = attendances.filter(a => a.status === 'full-day').length;
      const halfDays = attendances.filter(a => a.status === 'half-day').length;
      const absentDays = attendances.filter(a => a.status === 'absent').length;
      const pendingLeaves = leaves.filter(l => l.status === 'pending').length;
      const approvedLeaves = leaves.filter(l => l.status === 'approved').length;
      const activeTasks = tasks.filter(t => ['assigned', 'in-progress', 'review-pending'].includes(t.status)).length;

      setDashboardData({
        attendance: { fullDays, halfDays, absentDays },
        leaves: { pending: pendingLeaves, approved: approvedLeaves },
        activeTasks
      });
      
      setLeaveBalance(leaveBalanceRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      // Get today's date in IST
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istNow = new Date(now.getTime() + istOffset);
      const todayIST = istNow.toISOString().split('T')[0];
      
      // Fetch current month's attendance
      const month = istNow.getUTCMonth() + 1;
      const year = istNow.getUTCFullYear();
      
      const response = await api.get(`/employee/attendance/history?month=${month}&year=${year}`);
      
      // Find today's record by comparing dates in IST
      const todayRecord = response.data.attendances?.find(att => {
        const attDate = new Date(att.date);
        const attDateIST = new Date(attDate.getTime() + istOffset);
        const attDayIST = attDateIST.toISOString().split('T')[0];
        return attDayIST === todayIST;
      });
      
      setTodayAttendance(todayRecord || null);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      setTodayAttendance(null);
    }
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await api.post('/employee/attendance/checkin');
      await fetchTodayAttendance();
      await fetchDashboardData();
      window.dispatchEvent(new Event('attendance-updated'));
      alert('Checked in successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coordinates = await getCurrentCoordinates();

      await api.post('/employee/attendance/checkout', coordinates);
      await fetchTodayAttendance();
      await fetchDashboardData();
      window.dispatchEvent(new Event('attendance-updated'));
      alert('Checked out successfully!');
    } catch (error) {
      alert(getCheckoutErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="mt-8" />;
  }

  const attendanceStatus = getAttendanceStatus(todayAttendance);

  const statsCards = [
    {
      title: 'Full Days',
      value: dashboardData?.attendance?.fullDays || 0,
      icon: TrendingUp,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Half Days',
      value: dashboardData?.attendance?.halfDays || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Absent Days',
      value: dashboardData?.attendance?.absentDays || 0,
      icon: Calendar,
      color: 'bg-red-500',
      textColor: 'text-red-600'
    },
    {
      title: 'My Assigned Tasks',
      value: dashboardData?.activeTasks || 0,
      icon: FileText,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Your monthly overview</p>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Check In/Out Action */}
            <div className={`border-2 rounded-lg p-4 ${attendanceStatus.bgColor} ${attendanceStatus.borderColor}`}>
              <div className="flex flex-col items-center justify-center space-y-3">
                {!todayAttendance || !todayAttendance.checkInTime ? (
                  <button
                    onClick={handleCheckIn}
                    disabled={actionLoading}
                    className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <Clock className="h-5 w-5" />
                    <span>{actionLoading ? 'Processing...' : 'Check In'}</span>
                  </button>
                ) : (
                  <>
                    {!todayAttendance.checkOutTime ? (
                      <button
                        onClick={handleCheckOut}
                        disabled={actionLoading}
                        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <Clock className="h-5 w-5" />
                        <span>{actionLoading ? 'Processing...' : 'Check Out'}</span>
                      </button>
                    ) : (
                      <div className="w-full bg-green-500 text-white font-semibold py-3 px-6 rounded-lg text-center">
                        <Clock className="h-5 w-5 inline-block mr-2" />
                        Completed
                      </div>
                    )}
                    <div className="w-full text-sm space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Check In:</span>
                        <span className="font-semibold text-gray-900">{formatTime(todayAttendance.checkInTime)}</span>
                      </div>
                      {todayAttendance.checkOutTime && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Check Out:</span>
                            <span className="font-semibold text-gray-900">{formatTime(todayAttendance.checkOutTime)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="text-gray-600">Total Hours:</span>
                            <span className="font-bold text-blue-600">{todayAttendance.totalHours?.toFixed(2) || 0} hrs</span>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* View Tasks Action */}
            <button
              type="button"
              onClick={() => navigate('/employee/tasks')}
              className="group flex items-center justify-between p-4 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-center">
                <CheckCircle className="h-7 w-7 text-blue-500 mr-3" />
                <span className="text-blue-800 font-semibold">View Your Assigned Tasks</span>
              </div>
              <ArrowRight className="h-5 w-5 text-blue-500 transition-transform group-hover:translate-x-0.5" />
            </button>

            {/* Leave Application Action */}
            <button
              type="button"
              onClick={() => navigate('/employee/leave-application')}
              className="group flex items-center justify-between p-4 border border-indigo-200 bg-indigo-50 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-center">
                <Calendar className="h-7 w-7 text-indigo-500 mr-3" />
                <span className="text-indigo-800 font-semibold">Apply for Leave</span>
              </div>
              <ArrowRight className="h-5 w-5 text-indigo-500 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Leave Balance Cards */}
      {leaveBalance && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Leave Balance</h3>
          </div>
          <div className="p-6">
            {leaveBalance.probationStatus === 'probation' ? (
              <>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-5 border border-pink-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-pink-600 mb-1">Sick Leaves Left</p>
                        <p className="text-3xl font-bold text-pink-700">{leaveBalance.sick_leaves_left}</p>
                        <p className="text-xs text-pink-500 mt-1">this year</p>
                      </div>
                      <div className="p-3 bg-pink-200 rounded-full">
                        <Heart className="h-6 w-6 text-pink-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-5 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-amber-700 mb-1">Comp Off Left</p>
                        <p className="text-3xl font-bold text-amber-800">{leaveBalance.comp_off_left}</p>
                        <p className="text-xs text-amber-600 mt-1">FY {leaveBalance.comp_off_financial_year || leaveBalance.comp_off_month}</p>
                      </div>
                      <div className="p-3 bg-amber-200 rounded-full">
                        <Gift className="h-6 w-6 text-amber-700" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-800 mb-1">You're on probation</p>
                      <p className="text-xs text-amber-700">
                        {leaveBalance.probationEndDate
                          ? `Your probation period runs until ${new Date(leaveBalance.probationEndDate).toLocaleDateString()}. `
                          : ''}
                        Only Sick Leave and earned Comp Off are available during probation. Earned Leave will unlock once HR confirms you.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${leaveBalance.branch === 'SSbranch' ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-600 mb-1">Earned Leaves Left</p>
                        <p className="text-3xl font-bold text-green-700">{leaveBalance.earned_leaves_left}</p>
                        <p className="text-xs text-green-500 mt-1">accumulated balance</p>
                      </div>
                      <div className="p-3 bg-green-200 rounded-full">
                        <Umbrella className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-5 border border-pink-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-pink-600 mb-1">Sick Leaves Left</p>
                        <p className="text-3xl font-bold text-pink-700">{leaveBalance.sick_leaves_left}</p>
                        <p className="text-xs text-pink-500 mt-1">this year</p>
                      </div>
                      <div className="p-3 bg-pink-200 rounded-full">
                        <Heart className="h-6 w-6 text-pink-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-5 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-amber-700 mb-1">Comp Off Left</p>
                        <p className="text-3xl font-bold text-amber-800">{leaveBalance.comp_off_left}</p>
                        <p className="text-xs text-amber-600 mt-1">FY {leaveBalance.comp_off_financial_year || leaveBalance.comp_off_month}</p>
                      </div>
                      <div className="p-3 bg-amber-200 rounded-full">
                        <Gift className="h-6 w-6 text-amber-700" />
                      </div>
                    </div>
                  </div>

                  {leaveBalance.branch === 'SSbranch' && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-600 mb-1">Casual Leaves Left</p>
                          <p className="text-3xl font-bold text-purple-700">{leaveBalance.casual_leaves_left}</p>
                          <p className="text-xs text-purple-500 mt-1">this month</p>
                        </div>
                        <div className="p-3 bg-purple-200 rounded-full">
                          <Calendar className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-800 mb-1">Leave Balance Info</p>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• <strong>Earned Leaves:</strong> Credited once a year and carried forward to the next year</li>
                        <li>• <strong>Sick Leaves:</strong> Reset every calendar year and do not carry forward</li>
                        {leaveBalance.branch === 'SSbranch' && (
                          <li>• <strong>Casual Leaves:</strong> 1 day/month, resets monthly and does not carry forward</li>
                        )}
                        <li>• <strong>Comp Off:</strong> Earned from qualifying work on scheduled week-offs or holidays and usable during the same financial year after admin approval</li>
                        <li>• <strong>Note:</strong> If you exceed your balance, salary deductions will apply</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Leave Status */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Leave Status</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {dashboardData?.leaves?.pending || 0}
              </div>
              <div className="text-sm text-yellow-700">Pending Leaves</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {dashboardData?.leaves?.approved || 0}
              </div>
              <div className="text-sm text-green-700">Approved Leaves</div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
